import { bind, extend, get, isEmpty, filter, noop, property, result } from 'underscore';
import React, { useCallback, useContext, useEffect, useImperativeHandle, useMemo, useState } from 'react';
import PropTypes from 'prop-types';
import classNames from 'classnames';
import edm from '@bingads-webui-component-legacy/edm-campaign';
import jsdata from '@bingads-webui-component-legacy/js-data-campaign';
import { ThemeProvider } from 'react-jss';
import { ComponentSettings, ThemeFluent } from '@bingads-webui-theme-2018/theme-fluent';
import { AdStrengthRepository } from '@bingads-webui-campaign-react/responsive-search-ad-suggestion';
import { ResponsiveSearchAdEditor } from '@bingads-webui-campaign-react/responsive-search-ad-editor';
import { DependencyResolver } from '@bingads-webui-campaign/dependency-resolver';
import { AudienceAssociationView } from '@bingads-webui-campaign-legacy/audience-association/view/base-association-view';
import { Repository } from '@bingads-webui-campaign-legacy/adextension-image/service/repository';
import { ImageExtensionsPane } from '@bingads-webui-campaign-react/image-extensions-pane';
import { instrumentationScenarios } from '@bingads-webui-campaign/ad-creator-tools-instrumentation';
import { mergeI18n } from '@bingads-webui-campaign-react/i18n-merger';
import { RecommendationContext } from '@bingads-webui-campaign/responsive-search-ad-recommendation';
import { useAssetLibraryEventHandler } from '@bingads-webui-campaign-react/copilot-common';
import { testUurl, uurlLevels } from '@bingads-webui-component-legacy/upgraded-url/service/testUurl';
import { loadDisclaimerAssociations } from '@bingads-webui-campaign-react/search-campaign-data';
import { useDefaultStyles } from '@bingads-webui-react/with-default-styles';
import config from '@bingads-webui-component-legacy/config';
import dataModelUtil from '@bingads-webui-campaign-legacy/adextension-image/util/dataModelConvert';
import summaryDataContextManager from '@bingads-webui-static-legacy/summary/SummaryDataContextManager';
import instrumentation from '@bingads-webui-campaign-legacy/instrumentation';

import { getRSAStyles } from './rsa-styles';

DependencyResolver.set('campaignui/feature/audience/association/view/base-association-view', AudienceAssociationView);

const theme = { components: ComponentSettings, core: ThemeFluent };

export const RsaEditor = ({
  i18n,
  ad,
  onAdChanged,
  onAdExtensionsChanged,
  permissions,
  perfMarker,
  scope,
  serverContext,
  showAllValidationErrors,
  apiErrors,
  editorRef,
}) => {
  const classes = useDefaultStyles(getRSAStyles);
  const editMode = useMemo(() => get(ad, ['data', 'Id']), [ad]);

  const legacyI18n = useMemo(() => mergeI18n(
    window.globalLegacyI18n.get('@bingads-webui/i18n-amd-loader!campaignui/feature/audience-campaign'),
    i18n
  ), [i18n]);

  const adStrengthRepository = useMemo(() => new AdStrengthRepository({ edm, jsdata }), []);

  const imageExtensionsRepository = useMemo(() => {
    const repository = new Repository({
      schema: window.globalLegacySchema.get('ImageAdExtension'),
      afterChangeComitted: () => {
        summaryDataContextManager.updateContext(['AdGroupAdExtensionsGrid']);
      },
    });
    repository.association.createRequestFactory('adgroup');
    return repository;
  }, []);

  // eslint-disable-next-line @typescript-eslint/no-unused-vars
  const [cachedImageExtensionChanges, setCachedImageExtensionChanges] = useState({
    current: [],
    added: [],
    removed: [],
    updated: [],
  });

  const onImageExtensionChanged = useCallback((changes) => {
    setCachedImageExtensionChanges(changes);
    onAdExtensionsChanged(changes);
  }, [onAdExtensionsChanged]);

  // const [assetLibraryEventHandler, setAssetLibraryEventHandler] = useState({});
  const [{ postAssetEventLog = noop }] = useContext(RecommendationContext);
  const assetLibraryEventHandler = useAssetLibraryEventHandler(postAssetEventLog);

  const [previewImageCallback, setPreviewImageCallback] = useState(null);
  const savePreviewImageCallback = useCallback((func) => {
    setPreviewImageCallback(() => func);
  }, []);

  // eslint-disable-next-line @typescript-eslint/no-unused-vars
  const [logAppliedSuggestionsFunc, setLogAppliedSuggestionsFunc] = useState(null);
  const saveLogAppliedSuggestionsFunc = useCallback((func) => {
    setLogAppliedSuggestionsFunc(() => func);
  }, []);

  const onChanged = useCallback((data) => {
    onAdChanged({
      ...data,
      isValid: !data.incomplete && isEmpty(data.errors),
    });
  }, [onAdChanged]);

  const fetchDisclaimerInfo = useCallback(() => {
    if (scope.campaignId && permissions.IsDisclaimerForRSAEnabled) {
      return loadDisclaimerAssociations({
        cid: scope.customerId,
        aid: scope.accountId,
        campaignId: scope.campaignId,
      })
        .then(({ value }) => {
          if (value && value.length > 0) {
            return {
              hasDisclaimer: true,
              disclaimerData: get(value[0], 'AdExtension', {}),
            };
          }
          return { hasDisclaimer: false };
        })
        .catch(() => ({ hasDisclaimer: false }));
    }
    return Promise.resolve({ hasDisclaimer: false });
  }, [permissions.IsDisclaimerForRSAEnabled, scope.accountId, scope.campaignId, scope.customerId]);

  const triggerTestUurl = useCallback((fn, originalView) => {
    extend(originalView, {
      isInline: false,
      level: uurlLevels.Ad,
    });
    const params = (ad && ad.data) || {};
    const context = {
      ...scope,
      entityId: params.Id || '',
    };
    const options = {
      apiInLog: 'search-campaign/ad-editor/uurlTest',
    };
    testUurl(fn, originalView, params, context, options);
  }, [ad, scope]);

  const [activityLogger, setActivityLogger] = useState(null);
  useEffect(() => {
    const scenario = instrumentation[`ad${editMode ? 'Edit' : 'Creation'}ResponsiveSearch`].create();
    const logger = scenario[`ad${editMode ? 'Edit' : 'Creation'}`].create();
    logger.start();
    setActivityLogger(logger);

    return () => {
      logger.stop();
    };
  }, [editMode]);

  const logActivity = useCallback((message, apiName = 'ad editor') => {
    if (activityLogger) {
      const api = `${activityLogger.value} | ResponsiveSearch | campaignType: 1 | adGroupType: 1 | ${apiName}`;
      activityLogger.signal(message, api);
    }
  }, [activityLogger]);

  const renderImageExtension = useCallback((extraInfo) => {
    if (scope.adGroupId && activityLogger) {
      const imagePathFromConfig = config.get('imagePath');
      const maxImageExtensionsPerAdGroup = config.get('Feature').AdExtension.MaxSelectedImagesPerAdGroupOrCampaignForV2;
      const oDataConfig = {
        odataRootUrl: config.get('CampaignAzureDomain'),
        odataPath: config.get('odata.url') + config.get('odata.path'),
        odataToken: config.get('odata.token'),
      };

      return (<ImageExtensionsPane
        i18n={i18n}
        imageExtRepository={imageExtensionsRepository}
        adContext={scope}
        maxImageExtensionsPerAdGroup={maxImageExtensionsPerAdGroup}
        perfMarker={perfMarker}
        permissions={permissions}
        oDataConfig={oDataConfig}
        dataModelUtil={dataModelUtil}
        imagePath={imagePathFromConfig}
        cachedImageExtensionChanges={undefined}
        legacyI18n={legacyI18n}
        instrumentation={instrumentation}
        onPreviewImageChanged={previewImageCallback}
        adCreatorScenario={instrumentationScenarios.RSA}
        onImageExtensionChanged={onImageExtensionChanged}
        logActivity={logActivity}
        dateFormat={property(['CurrentUser', 'CultureDateTimeFormat', 'ShortDatePattern'])(serverContext)}
        timeZone={property(['CurrentAccount', 'TimeZone'])(serverContext)}
        activityLogger={activityLogger}
        isAADUser={result(serverContext, 'IsAADUser')}
        assetLibraryEventHandler={assetLibraryEventHandler}
        {...extraInfo}
      />);
    }
    return null;
  }, [activityLogger, assetLibraryEventHandler, i18n, imageExtensionsRepository, legacyI18n, logActivity, onImageExtensionChanged, perfMarker, permissions, previewImageCallback, scope, serverContext]);

  useImperativeHandle(editorRef, () => ({
    logAppliedSuggestions: (adData) => {
      if (logAppliedSuggestionsFunc) {
        const {
          Id,
          Headlines = [],
          Descriptions = [],
        } = adData;

        const currentAdExtension = (cachedImageExtensionChanges && cachedImageExtensionChanges.current) || [];
        const suggestedAdExtensions = filter(currentAdExtension, ext => ext.source === 'CopilotRecommendation');

        logAppliedSuggestionsFunc({
          entityId: Id,
          headlines: Headlines.map(t => t.Asset.Text),
          descriptions: Descriptions.map(t => t.Asset.Text),
          suggestedAdExtensions,
        });
      }
    },
  }));

  return (
    <ThemeProvider theme={theme}>
      <div className={classNames(classes.rsa, 'rsa-ad-editor-container')}>
        <div className="adEditor-container">
          <ResponsiveSearchAdEditor
            i18n={i18n}
            item={ad}
            errors={apiErrors}
            onChanged={onChanged}
            onFetchAdStrengthV2={bind(adStrengthRepository.fetchAdStrengthV2, adStrengthRepository)}
            onFetchDisclaimerInfo={fetchDisclaimerInfo}
            showActionBar={false}
            showInitialErrors={showAllValidationErrors}
            timeZone={property(['CurrentAccount', 'TimeZone'])(serverContext)}
            permissions={permissions}
            token={serverContext.Token}
            triggerTestUurl={triggerTestUurl}
            showUurlTestTool
            showFinalUrlSuffix
            showAdCustomizers
            showDynamicTextExtention
            edmCampaign={edm}
            jsDataCampaign={jsdata}
            isForVnext
            saveLogAppliedSuggestionsFunc={saveLogAppliedSuggestionsFunc}
            renderImageExtension={renderImageExtension}
            injectSetPreviewImage={savePreviewImageCallback}
            perfMarker={perfMarker}
            scope={scope}
            serverContext={serverContext}
            editMode={editMode}
            isNewTextAssetsButtonStyle
          />
        </div>
      </div>
    </ThemeProvider>
  );
};

RsaEditor.propTypes = {
  i18n: PropTypes.shape({}).isRequired,
  ad: PropTypes.shape({
    data: PropTypes.shape({}),
  }),
  onAdChanged: PropTypes.func.isRequired,
  onAdExtensionsChanged: PropTypes.func.isRequired,
  permissions: PropTypes.shape({
    IsDisclaimerForRSAEnabled: PropTypes.bool,
  }).isRequired,
  perfMarker: PropTypes.shape({}).isRequired,
  scope: PropTypes.shape({
    customerId: PropTypes.string,
    accountId: PropTypes.string,
    campaignId: PropTypes.string,
    adGroupId: PropTypes.string,
  }).isRequired,
  serverContext: PropTypes.shape({
    Token: PropTypes.string,
  }).isRequired,
  apiErrors: PropTypes.arrayOf(PropTypes.shape({})),
  showAllValidationErrors: PropTypes.bool,
  editorRef: PropTypes.shape({
    current: PropTypes.shape({}),
  }).isRequired,
};

RsaEditor.defaultProps = {
  ad: { data: {} },
  apiErrors: undefined,
  showAllValidationErrors: false,
};
